import packageMetadata from '../../../package.json' with { type: 'json' };
import { CHATFE_VERSION } from '../../chatfe/version.js';

import { createBestEffortBrowserUuid, resolveOtokocDeviceContext } from './deviceMetadata.js';

export type OtpFunnelClientEvent =
  | 'voice_lifecycle'
  | 'chat_lifecycle'
  | 'widget_metadata_received'
  | 'desktop_auto_open'
  | 'mobile_prompt_target'
  | 'mobile_prompt'
  | 'overlay'
  | 'otp_send_action'
  | 'otp_verify_action'
  | 'flow_terminated';

export type OtpFunnelClientOutcome =
  | 'attempted'
  | 'succeeded'
  | 'failed'
  | 'found'
  | 'missing'
  | 'mounted'
  | 'reattached'
  | 'opened'
  | 'closed'
  | 'abandoned'
  | 'reset'
  | 'superseded'
  | 'unknown';

export type OtpFunnelClientReason =
  | 'none'
  | 'target_absent'
  | 'target_replaced'
  | 'dismissed'
  | 'verified'
  | 'new_chat'
  | 'history_session_changed'
  | 'page_context_changed'
  | 'new_booking_cycle'
  | 'close_button_confirmed'
  | 'offline'
  | 'close_button'
  | 'end_button'
  | 'switch_to_chat'
  | 'page_unload'
  | 'widget_destroyed'
  | 'page_hidden'
  | 'duplicate_metadata'
  | 'out_of_order'
  | 'network_error'
  | 'unknown';

export interface OtpFunnelClientEventInput {
  event: OtpFunnelClientEvent;
  outcome: OtpFunnelClientOutcome;
  reason?: OtpFunnelClientReason;
  correlationId?: string | null;
  cycleId?: number | null;
  actionId?: string | null;
  threadId?: string | null;
  source?: 'booking' | 'voice' | 'text-lead' | 'unknown';
  dedupeKey?: string;
}

const WIDGET_VERSION = packageMetadata.version;
const SDK_VERSION = CHATFE_VERSION;
const CORRELATION_ID_RE = /^[a-f0-9]{32}$/u;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const MAX_DEDUPE_KEYS = 256;

let telemetryApiBase: string | null = null;
const emittedDedupeKeys = new Set<string>();

export function setOtpFunnelTelemetryApiBase(nextBaseUrl: string): void {
  telemetryApiBase = nextBaseUrl.replace(/\/$/, '');
}

export function createOtpFunnelActionId(): string {
  return createBestEffortBrowserUuid();
}

export function emitOtpFunnelClientEvent(input: OtpFunnelClientEventInput): void {
  try {
    if (input.dedupeKey && !rememberDedupeKey(input.dedupeKey)) {
      return;
    }

    const deviceContext = resolveOtokocDeviceContext();
    const eventId = createBestEffortBrowserUuid();
    const correlationId =
      typeof input.correlationId === 'string' && CORRELATION_ID_RE.test(input.correlationId)
        ? input.correlationId
        : undefined;
    const actionId =
      typeof input.actionId === 'string' && UUID_RE.test(input.actionId)
        ? input.actionId.toLowerCase()
        : undefined;
    const cycleId =
      typeof input.cycleId === 'number' &&
      Number.isFinite(input.cycleId) &&
      input.cycleId >= 0 &&
      input.cycleId <= 1_000_000_000
        ? Math.trunc(input.cycleId)
        : undefined;

    const apiBase = telemetryApiBase ?? `${window.location.origin}/api`;
    void fetch(`${apiBase}/telemetry/otp-funnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      keepalive: true,
      body: JSON.stringify({
        event: input.event,
        outcome: input.outcome,
        reason: input.reason ?? 'none',
        route: 'widget',
        source: input.source ?? 'booking',
        deviceType: deviceContext.deviceType,
        ...(correlationId ? { correlationId } : {}),
        ...(cycleId !== undefined ? { cycleId } : {}),
        ...(actionId ? { actionId } : {}),
        ...(typeof input.threadId === 'string' && input.threadId.trim()
          ? { threadId: input.threadId.trim().slice(0, 256) }
          : {}),
        eventId,
        widgetVersion: WIDGET_VERSION,
        sdkVersion: SDK_VERSION,
      }),
    }).catch(() => {
      // Optional telemetry must never affect the OTP UI.
    });
  } catch {
    // Storage, browser API, and serialization failures are non-critical.
  }
}

function rememberDedupeKey(key: string): boolean {
  const normalized = key.trim().slice(0, 128);
  if (!normalized || emittedDedupeKeys.has(normalized)) {
    return false;
  }
  emittedDedupeKeys.add(normalized);
  if (emittedDedupeKeys.size > MAX_DEDUPE_KEYS) {
    const oldest = emittedDedupeKeys.values().next().value;
    if (typeof oldest === 'string') {
      emittedDedupeKeys.delete(oldest);
    }
  }
  return true;
}

export function resetOtpFunnelClientTelemetryForTests(): void {
  emittedDedupeKeys.clear();
}
